import { useFeatureValue } from '@growthbook/growthbook-react';
import CookieConsent from '@ri-digital/web-component-cookie-consent';
import { getCookie } from '../analytics/helpers/getCookie/getCookie';

import '@ri-digital/web-component-cookie-consent/dist/index.css';
import classNames from 'classnames/bind';
import { Suspense } from 'react';
import { useQuery } from 'react-query';
import { Navigate, Route, Routes } from 'react-router-dom';

import style from './App.module.scss';
import Footer from './components/Footer';
import Header from './components/Header';
import LoadingOverlay from './components/LoadingOverlay';
import {
  useEmailState,
  useHasAccountState,
} from './helpers/hooks/usePersistedState';
import {
  useMode,
  useResetPasswordToken,
  useReturnUrl,
} from './helpers/hooks/useQueryParams';
import useSDKSync from './helpers/hooks/useSDKSync';
import useUniversalVariable from './helpers/hooks/useUniversalVariable';
import { isUSCountryCode } from './helpers/isUSCountryCode';
import { useFeaturesTimeout } from './hooks/useFeaturesTimeout';
import { httpClient } from './httpClient';
import {
  AccountCreationPage,
  CheckoutSignUpPage,
  EmailSubmissionPage,
  ForgottenPasswordPage,
  GuestCheckoutPage,
  GuestLoginPage,
  LoginPage,
  PasswordSignUpPage,
  PasswordValidationPage,
  RedirectPage,
  ResetPasswordPage,
} from './pages';
import { ErrorPage } from './pages/ErrorPage';
import { QueryKeys } from './query-keys';
import {
  accountCreation,
  checkoutSignUp,
  emailSubmission,
  forgottenPassword,
  guestCheckout,
  login,
  notFound,
  passwordSignUp,
  passwordValidation,
  redirect,
  resetPassword,
} from './routes';

import { WEB_VIEW_PARAM } from './constants';
import { useSessionToggle } from './hooks/useSessionToggle';
import useRecaptchaInit from './hooks/useRecaptchaInit';

const cx = classNames.bind(style);

const App = () => {
  const { data, isLoading } = useQuery(QueryKeys.features, () =>
    httpClient.get('/features'),
  );

  const mode = useMode();
  const [email] = useEmailState();
  const [hasAccount] = useHasAccountState();
  const returnUrl = useReturnUrl();
  const token = useResetPasswordToken();
  const hasLoginCookie = !!getCookie('qb_login');
  const signInJourney = email && hasAccount;
  const signUpJourney = email && !hasAccount;

  const isCookieConsentEnabled = isUSCountryCode()
    ? false
    : data?.features?.find((f) => f.name === 'EnableCookieConsent')?.enabled;

  const isGuestEnabled = useFeatureValue('guest-checkout', false);
  const isWebview = useSessionToggle(WEB_VIEW_PARAM);

  const shouldShowGuestLogin =
    isGuestEnabled && !isWebview && mode === 'checkout';

  useUniversalVariable(mode);

  // Initialize reCAPTCHA and reinitialize when GrowthBook feature changes
  useRecaptchaInit();

  useSDKSync();

  const hasTimedOut = useFeaturesTimeout(isLoading);
  if (!hasTimedOut) return <></>;

  const LoginPageComponent = shouldShowGuestLogin ? GuestLoginPage : LoginPage;

  return (
    <div className={`${cx('app-container')}`}>
      {!isLoading && isCookieConsentEnabled && <CookieConsent enabled />}
      <div className={`${cx('route-container')}`} data-testid="app-wrapper">
        <Suspense fallback={<LoadingOverlay loading />}>
          <Header />
          <Routes>
            {returnUrl && (
              <Route path={login.path} element={<LoginPageComponent />} />
            )}

            {returnUrl && shouldShowGuestLogin && (
              <Route
                path={guestCheckout.path}
                element={<GuestCheckoutPage />}
              />
            )}

            {returnUrl && (
              <Route
                path={emailSubmission.path}
                element={<EmailSubmissionPage />}
              />
            )}

            {returnUrl && signInJourney && (
              <Route
                path={passwordValidation.path}
                element={<PasswordValidationPage />}
              />
            )}

            {returnUrl && signInJourney && (
              <Route
                path={forgottenPassword.path}
                element={<ForgottenPasswordPage />}
              />
            )}

            {returnUrl && token && (
              <Route
                path={resetPassword.path}
                element={<ResetPasswordPage />}
              />
            )}

            {returnUrl && signUpJourney && mode === 'account' && (
              <Route
                path={accountCreation.path}
                element={<AccountCreationPage />}
              />
            )}

            {returnUrl && signUpJourney && mode === 'checkout' && (
              <Route
                path={checkoutSignUp.path}
                element={<CheckoutSignUpPage />}
              />
            )}

            {returnUrl &&
              signUpJourney &&
              mode === 'checkout' &&
              hasLoginCookie && (
                <Route
                  path={passwordSignUp.path}
                  element={<PasswordSignUpPage />}
                />
              )}

            {returnUrl && (
              <Route path={redirect.path} element={<RedirectPage />} />
            )}

            <Route path={notFound.path} element={<ErrorPage />} />

            {process.env.NODE_ENV === 'development' ? (
              <Route
                path="/connect/authorize/callback"
                element={<div>Dev only page</div>}
              />
            ) : null}

            <Route path="*" element={<Navigate to={notFound.path} />} />
          </Routes>
        </Suspense>
      </div>
      <Footer />
    </div>
  );
};

export default App;
